import Admin from "../Admin";
import React from "react";
import CardAdminBaseUnitTypeGeneral from "../../../Component/Card/Admin/BaseUnitType/CardAdminBaseUnitTypeGeneral";
import PageHeader from "../../../Component/PageHeader";
import MUIButton from "@mui/material/Button";
import MUIDeleteIcon from "@mui/icons-material/Delete";
import MUISaveButton from "@mui/icons-material/Save";
import Menu from "../../../Component/Menu";
import DialogConfirm from "../../../Component/Dialog/DialogConfirm";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import BaseUnitTypeService from "../../../Seating/Security/BaseUnitTypeService/baseUnitTypeService";
import Validator from "../../../Utility/Validator";
import BaseUnitType from "../../../Utility/Crud/BaseUnitType";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";

export default class AdminBaseUnitTypeCreateEdit extends Admin {
  constructor(props) {
    super(props);

    this.state.baseUnitType = null;
    this.state.isProcessing = false;
    this.state.isUpdate = !!props.match.params.id;
    this.state.confirmDialogOpen = false;
    this.state.validationIssues = {};
  }

  /**
   * Set the baseUnitType in the state.
   */
  afterComponentDidMount() {
    const baseUnitTypeId = this.props.match.params.id
      ? +this.props.match.params.id
      : null;
    if (baseUnitTypeId !== null) {
      this.setState({
        baseUnitType: this.context.baseUnitTypesIndexed[baseUnitTypeId],
      });
    }
  }

  /**
   * Creates the baseUnitType with the current properties in state.baseUnitType.
   */
  async createBaseUnitType() {
    this.setState({
      isProcessing: true,
    });

    return BaseUnitTypeService.createBaseUnitType(this.state.baseUnitType)
      .then((baseUnitType) => {
        this.context.updateIndexed("baseUnitTypesIndexed", baseUnitType);
        this.props.history.goBack();
        NotistackEnqueueSnackbar("Base Unit Type created successfully!", {
          variant: "success",
        });
      })
      .catch((err) => {
        // JZ. We need to test and make sure that Sentry still captures
        // errors when we catch them in the application.
        console.log(err);
        NotistackEnqueueSnackbar(
          "Oh no! There was an error creating a new Base Unit Type.",
          { variant: "error" },
        );
        this.setState({
          isProcessing: false,
        });
      });
  }

  /**
   * Updates the baseUnitType with the current properties in state.baseUnitType.
   */
  async updateBaseUnitType() {
    this.setState({
      isProcessing: true,
    });

    return BaseUnitTypeService.updateBaseUnitType(this.state.baseUnitType)
      .then((baseUnitType) => {
        this.context.updateIndexed("baseUnitTypesIndexed", baseUnitType);
        this.props.history.goBack();
        NotistackEnqueueSnackbar("Base Unit Type updated successfully!", {
          variant: "success",
        });
      })
      .catch((err) => {
        console.log(err);
        NotistackEnqueueSnackbar(
          "Oh no! There was an error updating the Base Unit Type.",
          { variant: "error" },
        );
        this.setState({
          isProcessing: false,
        });
      });
  }

  async deleteBaseUnitType() {
    this.setState({
      isProcessing: true,
    });

    return BaseUnitTypeService.deleteBaseUnitType(this.state.baseUnitType.id)
      .then(() => {
        this.context.deleteIndexed(
          "baseUnitTypesIndexed",
          this.state.baseUnitType.id,
        );
        this.props.history.goBack();
        NotistackEnqueueSnackbar("Base Unit Type deleted successfully!", {
          variant: "success",
        });
      })
      .catch((err) => {
        console.log(err);
        NotistackEnqueueSnackbar(
          "Oh no! There was an error deleting the Base Unit Type.",
          { variant: "error" },
        );
        this.setState({
          confirmDialogOpen: false,
        });
      });
  }

  /**
   * Render the content.
   */
  renderContent() {
    const handleChange = (baseUnitType) => {
      this.setState({
        baseUnitType: {
          ...this.state.baseUnitType,
          ...baseUnitType,
        },
      });
    };

    return (
      <>
        <CardAdminBaseUnitTypeGeneral
          baseUnitType={this.state.baseUnitType}
          onChange={handleChange.bind(this)}
          validationIssues={this.state.validationIssues}
        />
        <DialogConfirm
          open={this.state.confirmDialogOpen}
          onClose={() => this.setState({ confirmDialogOpen: false })}
          onConfirm={() => this.deleteBaseUnitType()}
          text="Are you sure you want to delete this Base Unit Type? This cannot be undone."
          header="Delete Base Unit Type"
          loading={this.state.isProcessing}
        />
      </>
    );
  }

  /**
   * Renders the header.
   */
  renderHeader() {
    let title;
    let subtitle;
    const buttonText = this.state.isUpdate ? "Save" : "Create";

    if (!this.state.isUpdate) {
      title = "Create Base Unit Type";
      subtitle = null;
    } else {
      title = "Edit Base Unit Type";
      subtitle =
        this.state.baseUnitType &&
        BaseUnitType.getDisplayName(this.state.baseUnitType) +
          " • " +
          this.state.baseUnitType.id;
    }

    const handleClickBack = () => {
      this.props.history.goBack();
    };

    const handleClickSave = () => {
      const validationIssues = Validator.validate(this.state.baseUnitType, {
        name: {
          required: true,
        },
      });

      this.setState({
        validationIssues: validationIssues,
      });

      if (Object.keys(validationIssues).length === 0) {
        if (this.state.isUpdate) {
          this.updateBaseUnitType();
        } else {
          this.createBaseUnitType();
        }
      } else {
        NotistackEnqueueSnackbar("Please correct the displayed issues.", {
          variant: "error",
        });
      }
    };

    const handleDelete = () => {
      this.setState({
        confirmDialogOpen: true,
      });
    };

    const buttons = [
      <MUIButton onClick={handleClickBack}>Back</MUIButton>,
      <MUIButton
        loading={this.state.isProcessing}
        loadingPosition="start"
        onClick={handleClickSave}
        variant="contained"
        startIcon={<MUISaveButton />}
      >
        {buttonText}
      </MUIButton>,
    ];

    if (this.state.isUpdate) {
      const menuItems = [
        <MUIMenuItem onClick={handleDelete}>
          <MUIListItemIcon>
            <MUIDeleteIcon fontSize="small" />
          </MUIListItemIcon>
          Delete
        </MUIMenuItem>,
      ];
      buttons.push(<Menu menuItems={menuItems} />);
    }

    return <PageHeader title={title} subtitle={subtitle} buttons={buttons} />;
  }
}
